import { MODAL_CREATE_API_KEY } from '@components/App/Modal/constants'
import { Button } from '@octopus-do/octopus-storybook'
import { Plus as PlusIcon } from '@octopus-do/octopus-storybook/icons'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useDispatch } from 'react-redux'

import {
  ApiKeyRow,
  ApiKeysSkeleton,
  DailyUsageChart,
  MonthlyUsage,
  UsageSkeleton,
} from './components'
import { MCP_DOCS_URL, PUBLIC_API_DOCS_URL } from './constants'
import { useApiKeys } from './hooks/useApiKeys'
import { useApiUsage } from './hooks/useApiUsage'
import { useRevokeApiKey } from './hooks/useRevokeApiKey'
import css from './styles.module.scss'

export const ApiKeys: React.FC = () => {
  const dispatch = useDispatch()
  const { keys, loading, error, refetch } = useApiKeys()
  const { revoke } = useRevokeApiKey()
  const {
    usage,
    loading: usageLoading,
    error: usageError,
    refetch: refetchUsage,
  } = useApiUsage()

  const openCreate = () =>
    dispatch(openModal({ modalType: MODAL_CREATE_API_KEY }))

  return (
    <div className={css.container}>
      <h1 className={css.title}>API keys</h1>

      <nav className={css.docs_navigation} aria-label="API documentation">
        <a
          className={css.docs_button}
          href={MCP_DOCS_URL}
          target="_blank"
          rel="noreferrer"
        >
          MCP server
        </a>
        <a
          className={css.docs_button}
          href={PUBLIC_API_DOCS_URL}
          target="_blank"
          rel="noreferrer"
        >
          Public API
        </a>
      </nav>

      <div className={css.usage}>
        {usageLoading && <UsageSkeleton />}

        {!usageLoading && usageError && (
          <div className={css.usage_error}>
            <div className={css.error}>Failed to load API usage</div>
            <Button
              priority="secondary"
              label="Try again"
              onClick={refetchUsage}
            />
          </div>
        )}

        {!usageLoading && !usageError && usage && (
          <>
            <MonthlyUsage monthly={usage.monthly} />
            <DailyUsageChart daily={usage.daily} />
          </>
        )}
      </div>

      <div className={css.intro}>
        <h2 className={css.section_title}>Active keys</h2>

        <div className={css.description}>
          Issue keys to access the Octopus public API. The full key is shown
          only once when created — store it somewhere safe.
        </div>
      </div>

      {loading && <ApiKeysSkeleton />}

      {!loading && error && (
        <div className={css.state}>
          <div className={css.error}>Failed to load API keys</div>
          <Button priority="secondary" label="Try again" onClick={refetch} />
        </div>
      )}

      {!loading && !error && (
        <div className={css.list}>
          {keys.length > 0 && (
            <>
              <div className={css.rows}>
                {keys.map((key) => (
                  <ApiKeyRow key={key.id} apiKey={key} onRevoke={revoke} />
                ))}
              </div>

              <div className={css.divider} />
            </>
          )}

          <Button
            size="medium"
            label="Create key"
            left={PlusIcon}
            onClick={openCreate}
          />
        </div>
      )}
    </div>
  )
}
